import {
  useRef,
  type HTMLAttributes,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { cn } from "@/lib/cn";

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  scrollLeft: number;
  active: boolean;
}

export function DragScroll({ className, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture, ...props }: HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<DragState | null>(null);
  const suppressClick = useRef(false);

  const finish = (event: ReactPointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (state?.active) {
      suppressClick.current = true;
    }
    drag.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture may already be gone.
    }
  };

  return (
    <div
      ref={ref}
      className={cn("drag-scroll-x no-scrollbar", className)}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        if (event.defaultPrevented || event.button !== 0) return;
        const el = ref.current;
        if (!el || el.scrollWidth <= el.clientWidth) return;
        drag.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          scrollLeft: el.scrollLeft,
          active: false,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        onPointerMove?.(event);
        const state = drag.current;
        const el = ref.current;
        if (!state || !el || state.pointerId !== event.pointerId) return;
        const dx = event.clientX - state.startX;
        const dy = event.clientY - state.startY;
        if (!state.active) {
          if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return;
          state.active = true;
        }
        event.preventDefault();
        el.scrollLeft = state.scrollLeft - dx;
      }}
      onPointerUp={(event) => {
        onPointerUp?.(event);
        finish(event);
      }}
      onPointerCancel={(event) => {
        onPointerCancel?.(event);
        finish(event);
      }}
      onClickCapture={(event) => {
        onClickCapture?.(event);
        if (!suppressClick.current) return;
        suppressClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
      {...props}
    />
  );
}
